import { StepDto } from 'workflow-definition/openapi';
import sumBy from 'lodash.sumby';
import {
  Action,
  Condition,
  StepConnection,
  StepCard,
  VariantAction,
  VariantStep,
} from './../models';
import { parseConditionals } from './workflow-mutations';
import {
  getNextConditionlessStep,
  getLastConditionlessStep,
} from './workflow-helpers';

const ACTION_LIST_ITEM_HEIGHT_PX = 76;
const ACTION_LIST_RENDERED_HEIGHT = 15;
const STEP_CARD_PANEL_TITLE_HEIGHT_PX = 32;
// The height of the Step Card's panel content without actions
const STEP_CARD_PANEL_CONTENT_BASE_HEIGHT_PX = 76;

// Positional vertical padding between cards
const VERTICAL_PADDING_BETWEEN_CARDS_PX = 150;

// Positional vertical padding between cards
const HORIZONTAL_PADDING_BETWEEN_CARDS_PX = 150;

// Positional vertical padding between cards
const CARD_WIDTH = 500;

type StepCardMapperFn = (
  dto: StepDto,
  indexDto: number,
  conditionalOrder: number,
  accumulatedStepCards: StepCard[]
) => StepCard;

export class StepMapper {
  static toStepCardNodesAndConnections(
    stepDtos: StepDto[],
    mapper: StepCardMapperFn
  ): {
    stepCards: StepCard[];
    stepConnections: StepConnection[];
  } {
    // This should be incremented every time a conditional StepConnection is added to the stepConnections array;
    // used for offseting the cards on the X axis.
    let conditionalOrder = 1;
    const stepConnections: StepConnection[] = [];
    const stepCards: StepCard[] = [];
    stepDtos.forEach((stepDto, indexDto) => {
      const stepCard = mapper(stepDto, indexDto, conditionalOrder, stepCards);

      if (stepCards.length >= 1) {
        const previousStepCard = stepCards[indexDto - 1];

        const hasConditional = stepCard?.conditionals?.length;

        // If this step has conditionals, we should step render of regular
        // connections and find the gap
        if (hasConditional) {
          const previousConditionlessStepCard = getLastConditionlessStep(
            stepDtos,
            indexDto
          );

          const nextConditionlessStepCard = getNextConditionlessStep(
            stepDtos,
            indexDto
          );

          if (nextConditionlessStepCard) {
            // Standard bypass: previous conditionless → next conditionless (skips this step)
            const connectionId = `${previousConditionlessStepCard?.id}-${nextConditionlessStepCard?.id}-connection`;
            stepConnections.push({
              id: connectionId,
              from: `${previousConditionlessStepCard?.id}-${previousConditionlessStepCard?.order}-output`,
              to: `${nextConditionlessStepCard?.id}-${nextConditionlessStepCard?.order}-input`,
              insertOrder: stepCard.order,
              conditional: false,
            });
          } else {
            // Conditional step is the last step — no next step to bypass to,
            // fall back to a regular connection from the previous step
            stepConnections.push({
              id: `${previousStepCard.id}-${stepCard.id}-connection`,
              from: previousStepCard.node.outputId,
              to: stepCard.node.inputId,
              insertOrder: stepCard.order,
              conditional: false,
            });
          }

          stepCard?.conditionals?.forEach((stepConditional: Condition) => {
            const connectionId = `${stepCard.node.inputId}-action-${stepConditional.data.if?.data}-connection`;
            stepConnections.push({
              id: connectionId,
              from: `action-${stepConditional.data.if?.data}-output`,
              to: stepCard.node.inputId,
              conditional: true,
            });
          });
          conditionalOrder = conditionalOrder + 1;
        } else {
          const connectionId = `${previousStepCard.id}-${stepCard.id}-connection`;
          stepConnections.push({
            id: connectionId,
            from: previousStepCard.node.outputId,
            to: stepCard.node.inputId,
            insertOrder: stepCard.order,
            conditional: false,
          });
        }
      }

      stepCards.push(stepCard);
    });

    return {
      stepCards,
      stepConnections,
    };
  }

  static fromDTO(
    dto: StepDto,
    indexDto: number,
    conditionalOrder: number,
    accumulatedStepCards: StepCard[]
  ): StepCard {
    const variant = dto.blockers
      ? VariantStep.BLOCKED
      : dto.conditionals
        ? VariantStep.CONDITIONAL
        : dto.isEnforcedOrder
          ? VariantStep.ENFORCED
          : dto.isRequired
            ? VariantStep.DEFAULT
            : VariantStep.OPTIONAL;

    const actions =
      dto.actions?.map(action => {
        const variant = action.blockers
          ? VariantAction.BLOCKED
          : action.conditionals
            ? VariantAction.CONDITIONAL
            : VariantAction.DEFAULT;
        return {
          id: action.id || -1,
          stepId: dto.id || -1,
          order: action.order ?? 0,
          labelLeft: action.name || '',
          labelRight: action.type || '',
          variant,
          isActionConditional: action.isActionConditional ?? false,
          conditionals: parseConditionals(action.conditionals),
          blockers: parseConditionals(action.blockers),
        };
      }) ?? [];

    const nodeId = `${dto.id}-node`;
    const outputId = `${dto.id}-${dto.order}-output`;
    const inputId = `${dto.id}-${dto.order}-input`;

    // Calculate the total height of all previous cards plus padding
    const previousCardsHeight = sumBy(
      accumulatedStepCards,
      function (s: StepCard) {
        return s.node.cardHeight + VERTICAL_PADDING_BETWEEN_CARDS_PX;
      }
    );

    const cardActionsHeight = actions.length * ACTION_LIST_ITEM_HEIGHT_PX;
    const cardHeight =
      STEP_CARD_PANEL_TITLE_HEIGHT_PX +
      STEP_CARD_PANEL_CONTENT_BASE_HEIGHT_PX +
      cardActionsHeight;

    // Calculate the absolute position on the y-axis for this card

    const negativeOffset = accumulatedStepCards.reduce(
      (sum, s) => sum + s.actions.length * ACTION_LIST_RENDERED_HEIGHT,
      0
    );

    const yPositionOffset = previousCardsHeight - negativeOffset;
    // Calculate the absolute position on the x-axis this card
    const xPositionOffset = dto.conditionals?.length
      ? (CARD_WIDTH / 2 + HORIZONTAL_PADDING_BETWEEN_CARDS_PX) *
        conditionalOrder
      : 0;
    const node = {
      id: nodeId,
      outputId,
      inputId,
      cardHeight,
      yPositionOffset,
      xPositionOffset,
    };

    return {
      id: dto.id || -1,
      index: indexDto,
      order: dto.order || 0,
      step: `Step ${indexDto + 1}:`,
      iconStep: 'pi pi-flag',
      title: dto.name || '',
      isVisible: dto.isVisible,
      isRequired: dto.isRequired,
      isEnforcedOrder: dto.isEnforcedOrder,
      isPrompt: dto.isPrompt,
      isForm: dto.form,
      trigger: dto.trigger,
      conditionals: parseConditionals(dto.conditionals),
      blockers: parseConditionals(dto.blockers),
      variant,
      actions: actions as unknown as Action[],
      node,
    };
  }
}
